import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Package, Plus, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

interface Muestra {
  id: string;
  folio: string;
  descripcion: string;
  marca: string | null;
  modelo: string | null;
  numeroSerie: string | null;
  imei: string | null;
  estado: string;
  custodioTipo: string;
  custodioReferencia: string | null;
  fechaSolicitud: string | null;
  fechaRecepcion: string | null;
  fechaEnvioLaboratorio: string | null;
  fechaRecepcionLaboratorio: string | null;
  fechaDevolucion: string | null;
  fechaResguardo: string | null;
  conformidadObservaciones: string | null;
  noConformidadMotivo: string | null;
  excepcionMotivo: string | null;
  guiaEnvio: string | null;
  notas: string | null;
  accionesDisponibles: string[];
}

const ESTADO_COLOR: Record<string, string> = {
  SOLICITADA:          "bg-estado-block-bg text-estado-block",
  RECIBIDA:            "bg-estado-info-bg text-estado-info",
  CONFORME:            "bg-estado-ok-bg text-estado-ok",
  NO_CONFORME:         "bg-estado-err-bg text-estado-err",
  ENVIADA_LABORATORIO: "bg-estado-info-bg text-estado-info",
  EN_LABORATORIO:      "bg-violet-100 text-violet-800",
  DEVUELTA:            "bg-estado-warn-bg text-estado-warn",
  RESGUARDADA:         "bg-stone-200 text-stone-700",
};

const ACCION_LABEL: Record<string, string> = {
  "recepcionar":                       "Recepcionar",
  "marcar-conforme":                   "Marcar conforme",
  "marcar-no-conforme":                "Marcar no conforme",
  "enviar-laboratorio":                "Enviar al laboratorio",
  "confirmar-recepcion-laboratorio":   "Confirmar recepción lab",
  "marcar-devuelta":                   "Marcar devuelta",
  "marcar-resguardada":                "Resguardar",
};

export function MuestrasSection({ solicitudId }: { solicitudId: string }) {
  const qc = useQueryClient();
  const [showAlta, setShowAlta] = useState(false);
  const [accionActiva, setAccionActiva] = useState<{ muestra: Muestra; accion: string } | null>(null);

  const lista = useQuery({
    queryKey: ["muestras", solicitudId],
    queryFn: async () =>
      (await api.get<Muestra[]>(`/api/sector/telecom/solicitudes/${solicitudId}/muestras`)).data,
  });

  return (
    <section className="rounded-lg border bg-surface p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-fg">
          <Package className="mr-1 inline h-4 w-4" /> Muestras
        </h2>
        <button
          onClick={() => setShowAlta(true)}
          className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-2.5 py-1.5 text-sm text-white transition hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" /> Registrar muestra
        </button>
      </div>

      {lista.isLoading && <p className="text-sm text-fg-subtle">Cargando…</p>}
      {lista.data && lista.data.length === 0 && (
        <p className="rounded border border-dashed p-4 text-center text-sm text-fg-subtle">
          Sin muestras registradas para esta solicitud.
        </p>
      )}
      {lista.data && lista.data.length > 0 && (
        <div className="w-full overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-xs uppercase tracking-wide text-fg-muted">
            <tr>
              <th className="px-3 py-2">Folio</th>
              <th className="px-3 py-2">Descripción</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Custodio</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {lista.data.map((m) => (
              <tr key={m.id} className="align-top">
                <td className="px-3 py-2 font-mono text-xs">{m.folio}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-fg">{m.descripcion}</div>
                  <div className="text-xs text-fg-subtle">
                    {[m.marca, m.modelo].filter(Boolean).join(" · ")}
                    {m.numeroSerie && <span> · SN {m.numeroSerie}</span>}
                  </div>
                  {m.noConformidadMotivo && (
                    <div className="mt-1 text-xs text-estado-err">No conforme: {m.noConformidadMotivo}</div>
                  )}
                  {m.excepcionMotivo && (
                    <div className="mt-1 text-xs text-estado-warn">
                      RN-208 excepción autorizada: {m.excepcionMotivo}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${
                      ESTADO_COLOR[m.estado] ?? "bg-estado-block-bg text-estado-block"
                    }`}
                  >
                    {m.estado}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-fg-muted">
                  <div>{m.custodioTipo}</div>
                  {m.custodioReferencia && (
                    <div className="text-fg-subtle">{m.custodioReferencia}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {m.accionesDisponibles.length === 0 ? (
                    <span className="text-xs text-fg-faint">—</span>
                  ) : (
                    <div className="flex flex-wrap justify-end gap-1">
                      {m.accionesDisponibles.map((a) => (
                        <button
                          key={a}
                          onClick={() => setAccionActiva({ muestra: m, accion: a })}
                          className="cursor-pointer rounded border bg-surface px-2 py-0.5 text-xs transition hover:bg-surface-2"
                        >
                          {ACCION_LABEL[a] ?? a}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {showAlta && (
        <AltaModal
          solicitudId={solicitudId}
          onClose={() => setShowAlta(false)}
          onSuccess={() => {
            setShowAlta(false);
            qc.invalidateQueries({ queryKey: ["muestras", solicitudId] });
          }}
        />
      )}

      {accionActiva && (
        <AccionModal
          solicitudId={solicitudId}
          muestra={accionActiva.muestra}
          accion={accionActiva.accion}
          onClose={() => setAccionActiva(null)}
          onSuccess={() => {
            setAccionActiva(null);
            qc.invalidateQueries({ queryKey: ["muestras", solicitudId] });
          }}
        />
      )}
    </section>
  );
}

function AltaModal({
  solicitudId,
  onClose,
  onSuccess,
}: {
  solicitudId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    descripcion: "",
    marca: "",
    modelo: "",
    numeroSerie: "",
    imei: "",
    notas: "",
  });
  const [error, setError] = useState<string | null>(null);
  const crear = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v.trim()) body[k] = v.trim();
      });
      await api.post(`/api/sector/telecom/solicitudes/${solicitudId}/muestras`, body);
    },
    onSuccess,
    onError: (err: AxiosError<{ detail?: string }>) =>
      setError(err.response?.data?.detail ?? "Error al registrar muestra"),
  });

  return (
    <Modal title="Registrar muestra" onClose={onClose}>
      <Field label="Descripción *" required>
        <input
          value={form.descripcion}
          onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        />
      </Field>
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Marca">
          <input
            value={form.marca}
            onChange={(e) => setForm((f) => ({ ...f, marca: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Modelo">
          <input
            value={form.modelo}
            onChange={(e) => setForm((f) => ({ ...f, modelo: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="Número de serie">
          <input
            value={form.numeroSerie}
            onChange={(e) => setForm((f) => ({ ...f, numeroSerie: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <Field label="IMEI">
          <input
            value={form.imei}
            onChange={(e) => setForm((f) => ({ ...f, imei: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
      </div>
      <Field label="Notas">
        <textarea
          rows={2}
          value={form.notas}
          onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        />
      </Field>
      {error && <ErrorBox text={error} />}
      <Footer
        onCancel={onClose}
        onConfirm={() => {
          if (!form.descripcion.trim()) {
            setError("La descripción es obligatoria");
            return;
          }
          setError(null);
          crear.mutate();
        }}
        confirmLabel="Registrar"
        loading={crear.isPending}
        disabled={!form.descripcion.trim()}
      />
    </Modal>
  );
}

function AccionModal({
  muestra,
  accion,
  onClose,
  onSuccess,
}: {
  solicitudId: string;
  muestra: Muestra;
  accion: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [body, setBody] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const config = configurarAccion(accion, muestra);

  const ejecutar = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {};
      Object.entries(body).forEach(([k, v]) => {
        if (v.trim()) payload[k] = v.trim();
      });
      // Endpoints sin body (confirmar recepción lab, resguardar) reciben {}.
      await api.post(
        `/api/sector/telecom/muestras/${muestra.id}/${accion}`,
        Object.keys(payload).length ? payload : {},
      );
    },
    onSuccess,
    onError: (err: AxiosError<{ detail?: string }>) =>
      setError(err.response?.data?.detail ?? "Error en la transición"),
  });

  return (
    <Modal title={config.titulo + " — " + muestra.folio} onClose={onClose}>
      {config.descripcion && <p className="text-sm text-fg-muted">{config.descripcion}</p>}
      {config.fields.map((f) => (
        <Field key={f.name} label={`${f.label}${f.required ? " *" : ""}`}>
          {f.tipo === "select" ? (
            <select
              value={body[f.name] ?? ""}
              onChange={(e) => setBody((b) => ({ ...b, [f.name]: e.target.value }))}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            >
              <option value="">Selecciona…</option>
              {f.opciones!.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : f.tipo === "textarea" ? (
            <textarea
              rows={2}
              value={body[f.name] ?? ""}
              onChange={(e) => setBody((b) => ({ ...b, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          ) : (
            <input
              value={body[f.name] ?? ""}
              onChange={(e) => setBody((b) => ({ ...b, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          )}
        </Field>
      ))}
      {error && <ErrorBox text={error} />}
      <Footer
        onCancel={onClose}
        onConfirm={() => {
          for (const f of config.fields) {
            if (f.required && !(body[f.name] ?? "").trim()) {
              setError(`Falta: ${f.label}`);
              return;
            }
          }
          setError(null);
          ejecutar.mutate();
        }}
        confirmLabel={config.titulo}
        loading={ejecutar.isPending}
      />
    </Modal>
  );
}

type FieldSpec = {
  name: string;
  label: string;
  required?: boolean;
  tipo?: "text" | "textarea" | "select";
  opciones?: Array<{ value: string; label: string }>;
  placeholder?: string;
};

function configurarAccion(accion: string, muestra: Muestra): {
  titulo: string;
  descripcion?: string;
  fields: FieldSpec[];
} {
  switch (accion) {
    case "recepcionar":
      return {
        titulo: "Recepcionar",
        descripcion: "Marca la muestra como recibida en NPMX. Custodia pasa a NPMX.",
        fields: [
          { name: "ubicacion", label: "Ubicación física", placeholder: "Ej. Bodega A, rack 12" },
          { name: "observaciones", label: "Observaciones de recepción", tipo: "textarea" },
        ],
      };
    case "marcar-conforme":
      return {
        titulo: "Marcar conforme",
        descripcion: "La muestra es apta para enviarse al laboratorio.",
        fields: [{ name: "observaciones", label: "Observaciones", tipo: "textarea" }],
      };
    case "marcar-no-conforme":
      return {
        titulo: "Marcar no conforme",
        descripcion:
          "RN-208: una muestra no conforme no puede enviarse al laboratorio sin excepción autorizada.",
        fields: [
          {
            name: "motivo",
            label: "Motivo de no conformidad",
            required: true,
            tipo: "textarea",
            placeholder: "Describir el daño o discrepancia",
          },
        ],
      };
    case "enviar-laboratorio":
      return {
        titulo: "Enviar al laboratorio",
        descripcion:
          muestra.estado === "NO_CONFORME"
            ? "RN-208: requiere excepción autorizada (el actor queda como autorizador)."
            : "Se enviará al laboratorio actualmente asignado a la solicitud.",
        fields: [
          { name: "guia", label: "Número de guía", required: true, placeholder: "EST-…" },
          { name: "acuse", label: "Acuse / paquetería" },
          ...(muestra.estado === "NO_CONFORME"
            ? [{
                name: "excepcionMotivo",
                label: "Motivo de la excepción (RN-208)",
                required: true,
                tipo: "textarea" as const,
              }]
            : []),
        ],
      };
    case "marcar-devuelta":
      return {
        titulo: "Marcar devuelta",
        descripcion: "Indica que el laboratorio devolvió la muestra y selecciona el destino.",
        fields: [
          {
            name: "destino",
            label: "Destino",
            required: true,
            tipo: "select",
            opciones: [
              { value: "NPMX", label: "NPMX (resguardo posterior posible)" },
              { value: "CLIENTE", label: "Cliente" },
            ],
          },
          { name: "observaciones", label: "Observaciones", tipo: "textarea" },
        ],
      };
    case "confirmar-recepcion-laboratorio":
      return {
        titulo: "Confirmar recepción en laboratorio",
        descripcion: "Marca la muestra como recibida físicamente por el laboratorio.",
        fields: [],
      };
    case "marcar-resguardada":
      return {
        titulo: "Resguardar",
        descripcion: "Solo aplica para muestras devueltas a NPMX. Cierra el ciclo de custodia.",
        fields: [],
      };
    default:
      return { titulo: accion, fields: [] };
  }
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg space-y-3 rounded-lg bg-surface p-4 shadow-overlay">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold text-fg">{title}</h3>
          <button onClick={onClose} className="cursor-pointer text-fg-faint transition hover:text-fg-muted">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-fg-muted">
        {label}
        {required && !label.includes("*") && <span className="text-estado-err"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded border border-estado-err/25 bg-estado-err-bg p-2 text-sm text-estado-err">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {text}
    </div>
  );
}

function Footer({
  onCancel,
  onConfirm,
  confirmLabel,
  loading,
  disabled,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  loading?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        onClick={onCancel}
        className="cursor-pointer rounded border bg-surface px-3 py-1.5 text-sm transition hover:bg-surface-2"
      >
        Cancelar
      </button>
      <button
        disabled={loading || disabled}
        onClick={onConfirm}
        className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
      >
        {confirmLabel}
      </button>
    </div>
  );
}
